import { useSegments } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TAB_BAR_GAP, TAB_BAR_HEIGHT } from '@constants/navigationConfig';
import { useUIStore } from '../store/uiStore';
import { useDesktopShell } from './useDesktopShell';

/**
 * The vertical space GlobalFloatingTabBar occupies at the bottom of the
 * screen, for anything that needs to sit clear of it (toasts, coachmarks,
 * the map's post hover card clamp). Returns 0 whenever the bar itself isn't
 * actually on screen, so callers don't have to duplicate the "is it even
 * showing" logic.
 *
 * Mirrors, deliberately, the two conditions `app/_layout.tsx` uses to decide
 * whether to mount `<GlobalFloatingTabBar />` at all:
 *   - `useDesktopShell()` — the desktop web shell replaces the floating pill
 *     with a persistent sidebar, so there's never a bar to clear there.
 *   - the route segment gate (`inAuthOrOnboarding` / `isFocusedRoute`) — the
 *     bar unmounts on `(auth)`/`(onboarding)` and on focused content routes
 *     (/post/*, /profile/*). /structure/checklist/[id] used to be one of
 *     these (a focused drill-in screen); it's now a redirect shim straight
 *     back to /structure/[id], which isn't focused, so it's dropped here too.
 * ...plus the ref-counted `uiStore.tabBarVisible` flag, which fades the
 * (still-mounted) bar out without unmounting it (e.g. while a sheet holds it
 * hidden). `app/_layout.tsx` isn't imported here (it's a screen, not a
 * module) — if that file's gating logic changes, update this alongside it.
 */
export function useTabBarClearance(): number {
  const insets = useSafeAreaInsets();
  const segments = useSegments();
  const isDesktopShell = useDesktopShell();
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);

  // Cast through string[] — same reasoning as app/_layout.tsx: expo-router's
  // typegen narrows useSegments() to a tuple that isn't generated in CI.
  const segs = segments as unknown as string[];
  const seg0 = segs[0];
  const inAuthOrOnboarding = seg0 === '(auth)' || seg0 === '(onboarding)';
  const isFocusedRoute = seg0 === 'post' || seg0 === 'profile';
  const isTabBarMounted = !isDesktopShell && !inAuthOrOnboarding && !isFocusedRoute;

  if (!isTabBarMounted || !tabBarVisible) return 0;

  // The flat bar sits flush to the bottom edge with the safe-area inset as
  // internal padding, so its footprint is inset + bar height.
  return insets.bottom + TAB_BAR_HEIGHT + TAB_BAR_GAP;
}
